/* eslint-disable class-methods-use-this */
import "element-internals-polyfill";
import { css, html, LitElement } from "lit";
import { customElement, state } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { close } from "../svg";
import { StateController } from "../state";
import { OrderByOptions } from "../orderby.enum";
import type { Filter, Option } from "./entities";

@customElement("umb-market-filter-panel")
export class UmbMarketFilterPanel extends LitElement {
  @state()
  private _filters?: Array<Filter> = [];

  #stateController: StateController;

  constructor() {
    super();
    this.#stateController = new StateController(this);
    this._filters = this.#stateController.state.filters;
  }

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener("filter-cleared", this.#handleFilterClear);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener("filter-cleared", this.#handleFilterClear);
  }

  // ensure state changes are synced back to slotted content
  #handleFilterClear = () => {
    this.requestUpdate();
  };

  #filterChange = (e: any, key: string) => {
    this.#stateController.setFilter(key, e.target.value);
  };

  #renderRadioButton(option: Option) {
    // Option is a lit type, so rather than extending it just to filter
    // out one radio, we'll do it by key here
    if (
      option.value === OrderByOptions.Relevance &&
      !StateController.isSearchView
    ) {
      return null;
    }

    return html`<uui-radio
      label="${option.name}"
      value="${option.value}"
      ?checked=${option.selected || false}
    ></uui-radio>`;
  }

  render() {
    return html`
      <div class="filter-panel">
        ${this._filters?.map(
          (filter) =>
            html`<umb-market-filter .filter=${filter}>
              ${when(
                filter.alias === "supportsUmbracoVersionNumber",
                () => html` <umb-market-version-filter
                  .filter=${filter}
                  @change=${(e: any) => this.#filterChange(e, filter.alias)}
                ></umb-market-version-filter>`,
                () => html` <uui-radio-group
                  name=${filter.alias}
                  @change=${(e: any) => this.#filterChange(e, filter.alias)}
                >
                  ${filter.options.map((o: Option) =>
                    this.#renderRadioButton(o)
                  )}
                </uui-radio-group>`
              )}</umb-market-filter
            > `
        )}
        <div id="filter-clear">
          <umb-market-button
            @click=${this.#stateController.clearFilters}
            title="Clear filters"
          >
            ${close}
          </umb-market-button>
        </div>
      </div>
    `;
  }

  static styles = [
    css`
      :host {
        display: block;

        --button-font-size: 13px;
        --button-line-height: normal;
      }

      uui-radio {
        --uui-radio-button-size: 24px;
      }

      .filter-panel {
        display: grid;
        grid-auto-columns: 1fr min-content;
        gap: 2px;
        margin-bottom: var(--xl-spacer);
      }

      umb-market-filter {
        grid-column: 1 / 2;
      }

      umb-market-filter:first-child {
        --border-top-left-radius: var(--s-spacer);
      }

      umb-market-filter:nth-last-child(2) {
        --border-bottom-right-radius: var(--s-spacer);
        --border-bottom-left-radius: var(--s-spacer);
      }

      #filter-clear {
        grid-column: 2 / 3;
        grid-row: 1;
        height: 100%;
      }

      #filter-clear umb-market-button {
        --button-padding: var(--s-spacer);
        --fill: var(--uui-color-text);
        --button-border-radius: 0 var(--s-spacer) var(--s-spacer) 0;
      }

      @media (min-width: 600px) {
        .filter-panel {
          grid-auto-columns: 1fr 1fr min-content;
        }

        umb-market-filter:nth-child(2n) {
          grid-column: 2 / 3;
        }

        #filter-clear {
          grid-column: 3 / 4;
        }

        umb-market-filter:first-child {
          --border-top-left-radius: var(--s-spacer);
        }

        umb-market-filter:nth-child(4),
        umb-market-filter:nth-last-child(2) {
          --border-bottom-right-radius: var(--s-spacer);
        }
      }

      @media (min-width: 1250px) {
        .filter-panel {
          grid-auto-columns: 1fr 1fr 1fr 1fr 1fr min-content;
        }

        umb-market-filter:nth-child(1) {
          grid-column: 1 / 2;
          --border-bottom-left-radius: var(--s-spacer);
        }

        umb-market-filter:nth-child(3) {
          grid-column: 3 / 4;
        }

        umb-market-filter:nth-child(4) {
          grid-column: 4 / 5;
          --border-bottom-right-radius: 0;
        }

        umb-market-filter:nth-child(5) {
          grid-column: 5 / 6;
          --border-bottom-left-radius: 0;
          --border-bottom-right-radius: 0;
        }

        #filter-clear {
          grid-column: 6 / 7;
        }
      }
    `,
  ];
}
